import { BackSide, LineSegments, BufferGeometry, LineBasicMaterial, Float32BufferAttribute, IcosahedronGeometry, Scene, PerspectiveCamera, WebGLRenderer, Group, Raycaster, Vector2, Vector3, MeshBasicMaterial, MathUtils, Mesh } from 'three';
import { Controls, Physics, Coin, Cloud, Time, Posts, Pane, Lights, Statistics, Sounds, State, Ball, config, levels, gradTexture, loadCloudTextures } from './index';
import { OrbitControls } from 'three/examples/jsm/controls/OrbitControls';
import { DRACOLoader } from 'three/examples/jsm/loaders/DRACOLoader';
import { GLTFLoader } from 'three/examples/jsm/loaders/GLTFLoader';
import { animate } from 'motion';
import { Text } from 'troika-three-text'
import * as CANNON from 'cannon-es';
import { Api } from './../game/index';
import { EffectComposer } from 'three/examples/jsm/postprocessing/EffectComposer.js'
import { RenderPass } from 'three/examples/jsm/postprocessing/RenderPass.js'

let globalUniforms = {
    time: { value: 0 },
    globalBloom: { value: 0 },
    noise: { value: null }
}


const noise = `
    float N21 (vec2 st) { 
        return fract( sin( dot( st.xy, vec2(12.9898,78.233 ) ) ) *  43758.5453123);
    }
    
    float smoothNoise( vec2 ip ){ 
      
    	vec2 lv = fract( ip );
      vec2 id = floor( ip );
      
      lv = lv * lv * ( 3. - 2. * lv );
      
      float bl = N21( id );
      float br = N21( id + vec2( 1, 0 ));
      float b = mix( bl, br, lv.x );
      
      float tl = N21( id + vec2( 0, 1 ));
      float tr = N21( id + vec2( 1, 1 ));
      float t = mix( tl, tr, lv.x );

      return clamp(mix( b, t, lv.y ) * 0.5 + 0.5, 0., 1.);
    }
    
    float smoothNoise2(vec2 p){

      p.y += time;
      p /= 4.;
      
      float n = smoothNoise(p) * 1.5;
      n += smoothNoise(p * 2.01) * 0.25;
      n += smoothNoise(p * 4.02) * 0.125;
      n += smoothNoise(p * 8.03) * 0.0625;
      n /= (1.5 + 0.25 + 0.125 + 0.0625);
      return clamp(n, 0., 1.);
    }
  `;

/**
 * Core game controller for the Gallagher Golden Goals kicking game.
 *
 * Owns the three.js scene, camera, renderer and post-processing passes,
 * the cannon-es physics world, game state/API bindings, and the level,
 * scoring, drag-to-launch and tutorial flows built on top of them.
 */
export default class Game {
    /**
     * @param {HTMLCanvasElement} canvas - Canvas element the renderer draws into.
     * @param {boolean} debug - Enables debug tooling (stats, dev controls) when true.
     */
    constructor(canvas, debug) {

        //Setup
        this.debug = debug;
        this.canvas = canvas;
        this.config = { ...config };

        // FPS monitoring
        this.fpsHistory = [];
        this.fpsCheckInterval = 60;
        this.frameCount = 0;
        this.lastFpsCheck = 0;
        this.pixelRatioAdjusted = false;
        this.lowFpsStreak = 0; // Track consecutive low FPS periods
        this.requiredLowFpsStreak = 5; // Require 8 seconds of low FPS

        // One-time tutorials
        this.hasSeenCoinTutorial = false;
        this.hasSeenWindTutorial = false;

        //Scene
        this.scene = new Scene();
        this.camera = new PerspectiveCamera(this.config.camera.perspective.fov, window.innerWidth / window.innerHeight, this.config.camera.near, this.config.camera.far);

        // Timing
        this.TIME = new Time(this);
        this.API = new Api();
        this.STATE = new State(this);
        this.launchTelemetry = null;


        // Initialisation
        this.setRenderer();
        this.setPasses()
        this.setBackground();
        this.setControls();
        this.setSounds();

        this.addFonts();

        // Await loaders
        this.init().then(() => {

            this.render();

            this.STATE.fetchState().then((data) => {

                this.STATE.hydrate(data);

                if (this.config.game.intro && this.STATE.getLevel() === 1 || !this.STATE.getTeam()) {
                    this.setIntro()
                } else {
                    this.changeLevels(this.STATE.getLevel());
                    this.updateScore();
                    this.STATE.updateNameElement();
                    this.STATE.updateTeamElement();
                    this.STATE.updateAvatarElement();
                }

            })
        });

    }

    /**
     * Monitor FPS and adjust pixel ratio if needed
     */
    monitorFPS() {
        const currentTime = performance.now();
        const deltaTime = currentTime - this.lastFpsCheck;

        if (deltaTime >= 1000) { // Check every second
            const fps = this.frameCount / (deltaTime / 1000);
            this.fpsHistory.push(fps);

            // Keep only last 10 seconds of FPS data
            if (this.fpsHistory.length > 10) {
                this.fpsHistory.shift();
            }

            // Check if current FPS is below threshold
            if (fps < 50) {
                this.lowFpsStreak++;
            } else {
                this.lowFpsStreak = 0; // Reset streak if FPS is good
            }

            // Only adjust if we've had sustained low FPS and haven't adjusted yet
            if (this.lowFpsStreak >= this.requiredLowFpsStreak && !this.pixelRatioAdjusted) {
                const avgFps = this.fpsHistory.reduce((a, b) => a + b, 0) / this.fpsHistory.length;

                console.log(`%c Sustained low FPS detected (${this.lowFpsStreak}s avg: ${avgFps.toFixed(1)}), adjusting pixel ratio to 1.2`, 'color: #FFA500');
                this.renderer.setPixelRatio(1.3);
                this.pixelRatioAdjusted = true;
            }

            this.frameCount = 0;
            this.lastFpsCheck = currentTime;
        }

        this.frameCount++;
    }

    /**
     * Build (or rebuild) the animated rain line mesh for the scene.
     * Disposes any previously created rain mesh before generating the new one.
     * @param {number} [intensity=0] - Rain intensity from 0 (none) upward; scales the line count.
     */
    addRain(intensity = 0) {
        let count = intensity * 10000;
        // Dispose previous mesh if it exists
        if (this.rainMesh) {
            this.scene.remove(this.rainMesh);
            this.rainMesh.geometry.dispose();
            this.rainMesh.material.dispose();
            this.rainMesh = null;
        }

        const gPos = new Float32Array(count * 6); // 2 points per line * 3 components
        const gEnds = new Float32Array(count * 4); // 2 components per point * 2 points

        for (let i = 0; i < count; i++) {
            const x = MathUtils.randFloatSpread(50);
            const y = MathUtils.randFloat(-5, 10);
            const z = MathUtils.randFloatSpread(50);
            const len = MathUtils.randFloat(0.25, 0.5);

            const pIdx = i * 6;
            const eIdx = i * 4;

            // Same start and end point (will be adjusted in vertex shader)
            gPos.set([x, y, z, x, y, z], pIdx);
            gEnds.set([0, len, 1, len], eIdx);
        }

        const geometry = new BufferGeometry();
        geometry.setAttribute("position", new Float32BufferAttribute(gPos, 3));
        geometry.setAttribute("gEnds", new Float32BufferAttribute(gEnds, 2));

        const material = new LineBasicMaterial({
            color: 0x6EB0D9,
            transparent: true,
            onBeforeCompile: shader => {
                shader.uniforms.time = globalUniforms.time;
                shader.uniforms.noiseTex = globalUniforms.noise;
                shader.uniforms.globalBloom = globalUniforms.globalBloom;

                shader.vertexShader = `
                    uniform float time;
                    uniform sampler2D noiseTex;
                    attribute vec2 gEnds;
                    varying float vGEnds;
                    varying float vH;
                    ${shader.vertexShader}
                                `.replace(
                    `#include <begin_vertex>`,
                    `#include <begin_vertex>
                    vec3 pos = position;
                    vec2 nUv = (vec2(pos.x, -pos.z) + 25.0) / 50.0;
                    float h = texture2D(noiseTex, nUv).g;
                    h = (h - 0.5) * 4.0;
                                
                    pos.y = -mod(10.0 - (pos.y - time * 5.0), 15.0) + 10.0;
                    h = pos.y - h;
                    pos.y += gEnds.x * gEnds.y;
                    transformed = pos;
                    vGEnds = gEnds.x;
                    vH = smoothstep(3.0, 0.0, h);`
                );

                shader.fragmentShader = `
                    uniform float time;
                    uniform float globalBloom;
                    varying float vGEnds;
                    varying float vH;
                    ${noise}
                    ${shader.fragmentShader}
                                `.replace(
                    `vec4 diffuseColor = vec4( diffuse, opacity );`,
                    `
                    float op = pow(1.0 - vGEnds, 3.0);
                    float h = pow(vH, 3.0) * 0.5 + 0.5;
                    vec3 col = diffuse * h;
                    col *= 1.0 + smoothstep(0.99, 1.0, h);
                    vec4 diffuseColor = vec4(col, op);`
                );
            }
        });

        const lines = new LineSegments(geometry, material);
        this.scene.add(lines);
        this.rainMesh = lines;
    }
    /**
     * Detect if device is slow/low-end
     */
    isSlowDevice() {
        const is_mobile = /Android|webOS|iPhone|iPad|iPod|BlackBerry|IEMobile|Opera Mini/i.test(navigator.userAgent);
        const low_core_count = navigator.hardwareConcurrency && navigator.hardwareConcurrency <= 2;
        const low_memory = navigator.deviceMemory && navigator.deviceMemory <= 2;
        const is_older_ios = /iPad|iPhone|iPod/.test(navigator.userAgent) && !window.MSStream;
        return is_mobile || low_core_count || low_memory || is_older_ios;
    }

    /**
     * Set up the renderer
     */
    setRenderer() {
        this.renderer = new WebGLRenderer({
            canvas: this.canvas,
            antialias: true,
            powerPreference: 'high-performance'
        });
        this.renderer.setSize(window.innerWidth, window.innerHeight);
        this.renderer.setPixelRatio(2);
        this.renderer.setAnimationLoop(null);
        if (this.config.shadows) this.renderer.shadowMap.enabled = true;
        if (this.config.shadows) this.renderer.shadowMap.type = PCFSoftShadowMap;
    }


    /**
     * Set the debug mode
     */
    setDebug() {
        if (!this.debug) return;
        this.config.game.intro = false;
        this.config.game.debug = true;
        this.config.game.stats = true;
        this.config.game.music = false;
        this.CONTROLS = new Controls(this, window.levels);
        this.STATS = new Statistics();
    }


    /**
     * Set up the passes
     */
    setPasses() {
        this.passes = {};
        this.passes.composer = new EffectComposer(this.renderer);
        this.passes.renderPass = new RenderPass(this.scene, this.camera);
        this.passes.composer.addPass(this.passes.renderPass);
    }

    /**
     * Console log wrapper that only outputs when debug mode is enabled.
     * @param {*} message - Primary message to log.
     * @param {...*} optionalParams - Additional values passed through to console.log.
     */
    log(message, ...optionalParams) {
        if (this.debug) {
            console.log(message, ...optionalParams);
        }
    }

    /**
     * Set up the controls
     */
    setControls() {
        this.controls = new OrbitControls(this.camera, this.renderer.domElement);
        this.controls.enableDamping = true;
        this.controls.minDistance = this.config.camera.distance.min;
        this.controls.maxDistance = this.config.camera.distance.max;
        this.controls.minPolarAngle = this.config.camera.polar.min;
        this.controls.maxPolarAngle = this.config.camera.polar.max;
        this.controls.update();
    }


    /**
     * Set up the sound system
     */
    setSounds() {
        this.SOUNDS = new Sounds();
        if (this.config.game.music) this.SOUNDS.playNextTrack();
    }


    /**
     * Set up the background gradient
     */
    setBackground() {
        this.scene.add(new Mesh(new IcosahedronGeometry(this.config.sky.radius, this.config.sky.detail), new MeshBasicMaterial({
            map: gradTexture([this.config.sky.stops, this.config.sky.colours]),
            side: BackSide,
            depthWrite: false,
        })));
    }



    /**
     * Load all game meshes/assets and wire up event handlers.
     * Awaited in the constructor before the render loop starts.
     * @returns {Promise<void>}
     */
    async init() {
        await this.addMeshes();
        this.setEventHandlers();
    }

    /**
     * Position the camera/controls for the intro sequence and flag the
     * document body as being in the intro state.
     */
    setIntro() {
        document.body.classList.add('intro');
        this.camera.position.set(0, 1, 0);
        this.controls.target.y += 21;
        this.controls.target.z += -14;
        this.controls.update();
    }

    /**
     * Clear persisted game state, reset score/level UI and the scene, optionally reloading the page.
     * @param {boolean} [reload=false] - When true, reloads the browser window after clearing state.
     */
    clearState(reload = false) {
        this.STATE.clearState();
        this.updateScore();
        this.changeLevels(this.STATE.getLevel());
        this.reset();
        if (reload) window.location.reload();
        console.log(`%c Level: ${this.STATE.getLevel()} Score: ${this.STATE.getScore()}`, "color:#fff;font-size:12px; padding:0.45rem 0.75rem; margin: 0rem auto 1rem auto; font-family: Helvetica, sans-serif; border: 2px solid #0dd8d8; border-radius: 4px;font-weight: 100;background-size: cover;background-repeat: no-repeat;border: double 1px transparent;background-image: linear-gradient(#282828, #282828), radial-gradient(circle at top left, #2563EB, #82a0e2);background-origin: border-box;background-clip: padding-box, border-box;");
    }

    /**
     * Animate the on-screen score element from its current value to the
     * player's latest score held in state.
     */
    updateScore() {

        const current = parseInt(document.querySelector('.js-score').textContent) || 0;
        const target = this.STATE.getScore() || 0;

        this.STATE.setScore(target);

        animate(current, target, {
            duration: 1,
            onUpdate: latest => {
                document.querySelector('.js-score').textContent = Math.floor(latest);
            }
        });
    }


    /**
     * Create and configure the troika-three-text countdown label used for the shot clock.
     */
    addFonts() {
        this.text = new Text();
        this.scene.add(this.text);
        this.text.font = this.config.constants.asset_directory + 'font.ttf'
        this.text.text = '10'
        this.text.fontSize = 1.4;
        this.text.anchorY = 'center';
        this.text.anchorX = 'center';
        this.text.position.y = 6;
        this.text.position.z = -34.6;
        this.text.outlineWidth = 0.02;
        this.text.color = 0x5CACD8;
        this.text.outlineColor = 0x2C3947;
        this.text.sync();
    }

    /**
     * Load all GLTF models and cloud textures, build the scene's meshes/groups
     * (logo, floor, arrow, ring, cup, ball, clouds) and instantiate the
     * gameplay subsystems (Ball, Posts, Pane, Lights, Physics).
     * @returns {Promise<void>}
     */
    async addMeshes() {

        const loader = new GLTFLoader();
        const dracoLoader = new DRACOLoader();
        const { asset_directory } = this.config.constants;

        dracoLoader.setDecoderPath('https://www.gstatic.com/draco/v1/decoders/');
        loader.setDRACOLoader(dracoLoader);

        const [logo, floor, arrow, ring, cup, ball, cloudTextures] = await Promise.all([
            loader.loadAsync(asset_directory + "logo-combined.glb"),
            loader.loadAsync(asset_directory + "ground-spark.glb"),
            loader.loadAsync(asset_directory + "arrow.glb"),
            loader.loadAsync(asset_directory + "ring.glb"),
            loader.loadAsync(asset_directory + "cup.glb"),
            loader.loadAsync(asset_directory + "ball.glb"),
            loadCloudTextures(asset_directory),
        ]);

        this.logo = logo.scene;
        this.logo.position.y = 20;
        this.logo.position.z = -15;

        this.floor = floor.scene;
        this.ring = ring.scene;
        this.cup = cup.scene;

        this.floor.traverse((child) => {
            if (child.isMesh) {
                if (this.config.shadows) child.receiveShadow = true;
                child.castShadow = false;
            }
        })

        this.clouds = [
            ...Cloud.createField(this, cloudTextures, this.config.cloudsBack),
            ...Cloud.createField(this, cloudTextures),
            ...Cloud.createFixedField(this, cloudTextures, this.config.pitchClouds),
        ];

        this.clouds.forEach((cloud) => {
            this.scene.add(cloud.mesh);
            if (cloud.debugHelper) this.scene.add(cloud.debugHelper);
        });

        this.arrowPivot = new Group();
        this.arrow = arrow.scene;

        this.arrow.rotation.set(0, Math.PI, 0);
        this.arrow.position.y = 0.009;
        this.arrowPivot.add(this.arrow);

        this.ball_logo = ball.scene.clone();

        this.ballLogoGroup = new Group();
        this.scene.add(this.ballLogoGroup);

        this.ballLogoGroup.add(this.ball_logo);
        this.ballLogoGroup.position.set(.55, 20.5, -14.95);
        this.ballLogoGroup.rotation.set(1.6, 2.4, -.1);
        this.ball_logo.rotation.set(0, 0, 0);
        this.ball_logo.position.set(0, -.17, 0.5);

        this.TIME.addEventListener('tick', () => {
            this.ballLogoGroup.rotation.z += 0.009;
        });


        if (window.innerWidth < 768) {
            this.logo.position.z = -18;
            this.ballLogoGroup.position.z = -18.05;
        }

        this.BALL = new Ball(ball, this);
        this.POSTS = new Posts(this);
        this.PANE = new Pane(this);
        this.LIGHTS = new Lights(this);
        this.PHYSICS = new Physics(this);

        this.scene.add(this.logo, this.floor, this.ring, this.arrowPivot, this.BALL.object, this.cup);
    }

    /**
     * Compute a smoothly time-varying wind force vector for the current level.
     * @param {'l'|string} direction - Wind direction; `'l'` maps to 180°, anything else to 0°.
     * @param {{min?: number, max?: number}} intensity - Min/max wind intensity range.
     * @returns {Vector3} Wind force vector (y is always 0).
     */
    calculateWindForce(direction, intensity) {

        const min = intensity.min ?? 0;
        const max = intensity.max ?? 0;
        const dir = direction === 'l' ? 180 : 0;

        // Use time-based smooth variation instead of pure random
        const time = this.TIME.elapsed * 0.001; // Convert to seconds
        const smoothRandom = (Math.sin(time * 0.5) + Math.sin(time * 0.3) * 0.5) * 0.5 + 0.5;
        const smoothIntensity = min + (max - min) * smoothRandom;

        const rad = MathUtils.degToRad(dir);
        return new Vector3(Math.cos(rad) * smoothIntensity, 0, Math.sin(rad) * smoothIntensity);
    }


    /**
     * @param {number} min - Inclusive lower bound.
     * @param {number} max - Exclusive upper bound.
     * @returns {number} A random float between min and max.
     */
    getRandom(min, max) {
        return Math.random() * (max - min) + min;
    }

    /**
     * Reset the ball, cup, arrow, camera and coins to the current level's
     * starting position ready for a new shot.
     */
    reset() {

        this.ring_hit = false;

        // reset the shot clock
        this.TIME.resetTime();

        let position = (this.level_data) ? { ...this.level_data.position } : { ...this.config.positions.reset };


        function convertXNumber(value) {
            const inputMin = 0;
            const inputMax = 80;
            const outputMin = -9.5;
            const outputMax = 9.5;

            const ratio = (value - inputMin) / (inputMax - inputMin);
            return outputMin + ratio * (outputMax - outputMin);
        }


        function convertZNumber(value) {
            const inputMin = 0;
            const inputMax = 100;
            const outputMin = -20;
            const outputMax = 26;

            const ratio = (value - inputMin) / (inputMax - inputMin);
            return outputMin + ratio * (outputMax - outputMin);
        }

        // Sanitise the positioning data
        position.x = convertXNumber(position.x);
        position.z = convertZNumber(position.z);

        this.BALL.body.type = CANNON.Body.STATIC;
        this.BALL.body.mass = 0;
        this.BALL.body.updateMassProperties();

        ['velocity', 'force', 'torque', 'angularVelocity'].forEach(key => { this.BALL.body[key].set(0, 0, 0) });

        this.BALL.body.position.copy(new CANNON.Vec3(position.x, position.y, position.z));

        this.BALL.body.quaternion.setFromAxisAngle(new CANNON.Vec3(1, 0, 0), Math.PI / 2);

        this.ballInMotion = false;

        this.cup.position.set(position.x, 0, position.z);

        this.arrowPivot.position.set(position.x, -.069, position.z);

        const estimateMagic = (x, z) => {
            if (x === 0) return 0;
            const absX = Math.abs(x);
            const base = 10;
            const sign = Math.sign(x);
            const factor = z <= 0
                ? (base + Math.abs(z) * 0.8)
                : (base - z * 0.25);
            return (absX / 5) * factor * sign;
        }

        if (true) {
            this.controls.target.copy(new Vector3(position.x, position.y, position.z)).y += 0.5;
            this.camera.position.set(estimateMagic(position.x, position.z), 1.3, 50);
            this.controls.update();
        }

        this.setCoins();

        this.hasHit = false;

        this.isHandled = false;

    }

    /**
     * Destroy any existing coins and (re)create them from the current level's coin data.
     */
    setCoins() {


        if (this.coins?.length) {
            this.coins.forEach(coin => coin.destroy?.());
            this.coins.length = 0;
        } else {
            this.coins = [];
        }

        // this.level_data.coins = ['H', 'M', 'L'];
        // If no coin data, exit early
        if (!this.level_data?.coins?.length) return;

        // Create new coins
        for (const data of this.level_data.coins) {
            this.coins.push(new Coin(this, data));
        }
    }

    /**
     * Start a fresh kick game via the API, hydrate state from the response,
     * and reset the scene to the resulting level.
     */
    restart() {
        this.STATE.startKickGame().then((data) => {
            this.STATE.setLevel((data.data.current_kick_level_number) ? data.data.current_kick_level_number : 1);
            this.STATE.setName(data.data.nickname);
            this.STATE.setTeam(data.data.team);
            this.STATE.setScore(data.data.total_kick_points);

            this.STATE.updateAvatarElement();

            this.changeLevels(this.STATE.getLevel(), false);
            this.updateScore();
            this.reset();

        });
    }

    /**
     * Kick off a new game from the intro/name-entry screen: reads the player's
     * name and chosen team from the DOM, starts the kick game via the API,
     * transitions the UI out of the intro state, and begins the first level's
     * horizontal-swipe tutorial.
     */
    start() {

        this.STATE.setName(document.querySelector('.js-name').value);
        this.STATE.setTeam(document.querySelector('.js-slider-team .swiper-slide-active').dataset.team);

        this.STATE.startKickGame().then((data) => {

            this.STATE.setLevel((data.data.current_kick_level_number) ? data.data.current_kick_level_number : 1);
            this.STATE.setName(data.data.nickname);
            this.STATE.setTeam(data.data.team);
            this.STATE.setScore(data.data.total_kick_points);

            this.STATE.updateAvatarElement();

            document.body.classList.add('started');
            document.body.classList.remove('teamSliderVisible');
            console.log(`%c Starting the game`, 'color: #A6E3A1');

            this.tween(this.controls.target, 'y', 21, 1, 2, 'easeOut');
            this.tween(this.camera.position, 'y', 21, 1, 2, 'easeOut');

            this.changeLevels(this.STATE.getLevel(), false);
            this.updateScore();

            this.TIME.pause();

            setTimeout(() => {
                this.config.game.intro = false;
                this.logo.visible = false;
                this.logo.position.z = 180;
                this.ballLogoGroup.visible = false;
                document.body.classList.add('tutorial-horizontal-active');
            }, 2000);

        })

    }

    /**
     * Animate a numeric property on an object from `start` to `end`.
     * @param {Object} object - Target object whose property will be mutated.
     * @param {string} property - Name of the property to animate.
     * @param {number} start - Starting value.
     * @param {number} end - Ending value.
     * @param {number} duration - Duration of the animation, in seconds.
     * @param {string} [easing='linear'] - Motion easing function name.
     */
    tween(object, property, start, end, duration, easing = 'linear') {
        animate(start, end, {
            duration,
            easing: easing,
            onUpdate: (latest) => object[property] = latest
        });
    }

    /**
     * Wire up all DOM/window/renderer event listeners for the game: tab
     * title flashing on blur, window resize, the start button, arrow
     * hover/drag-to-launch pointer handling, the per-tick shot clock display,
     * and tutorial-overlay dismissal clicks.
     */
    setEventHandlers() {

        let interval = null;


        window.addEventListener('blur', () => {

            const steps = 10;
            const titles = Array.from({ length: steps }, (_, i) => `${'_'.repeat(i)}🏉${'_'.repeat(steps - i - 1)}🏆`).concat('___________✅');
            let count = 0;

            interval = setInterval(() => {
                document.title = titles[count % titles.length];
                count++;
            }, 333);

        });

        window.addEventListener('focus', () => {

            document.title = 'Gallagher Golden Goals';
            clearInterval(interval);
            interval = null;

        });

        window.addEventListener('resize', () => {
            const width = window.innerWidth;
            const height = window.innerHeight;
            this.renderer.setSize(width, height);

            this.camera.aspect = width / height;
            this.camera.updateProjectionMatrix();
        });

        let $start = document.querySelector('.js-start');

        if ($start) {
            $start.addEventListener('click', (e) => {
                e.preventDefault();
                this.start();
            });
        }

        const raycaster = new Raycaster();
        const pointer = new Vector2();
        let dragStartY = 0;
        let dragEndY = 0;
        let maxDragDistanceUp = 0; // Maximum drag distance for upward drag
        let maxDragDistanceDown = 0.4; // Maximum drag distance for downward drag

        const getCanvasPoint = (event, rect) => {
            const touch = event.touches && event.touches[0];
            const clientX = touch ? touch.clientX : event.clientX;
            const clientY = touch ? touch.clientY : event.clientY;

            return {
                x: Number(((clientX - rect.left) / rect.width).toFixed(4)),
                y: Number(((clientY - rect.top) / rect.height).toFixed(4)),
            };
        };

        // change cursor when hovering over the arrow by adding a class to the body
        this.renderer.domElement.addEventListener('mousemove', (event) => {
            if (this.TIME.paused) return;
            const rect = this.renderer.domElement.getBoundingClientRect();
            pointer.x = ((event.clientX || event.touches[0].clientX) - rect.left) / rect.width * 2 - 1;
            pointer.y = -((event.clientY || event.touches[0].clientY) - rect.top) / rect.height * 2 + 1;
            raycaster.setFromCamera(pointer, this.camera);
            const intersects = raycaster.intersectObject(this.arrowPivot, true);
            if (intersects.length > 0) {
                this.renderer.domElement.style.cursor = 'grab';
                document.body.classList.add('grab');
            } else {
                this.renderer.domElement.style.cursor = 'default';
                document.body.classList.remove('grab');
            }
        });

        const startDrag = (event) => {
            if (this.TIME.paused) return;
            const rect = this.renderer.domElement.getBoundingClientRect();
            pointer.x = ((event.clientX || event.touches[0].clientX) - rect.left) / rect.width * 2 - 1;
            pointer.y = -((event.clientY || event.touches[0].clientY) - rect.top) / rect.height * 2 + 1;
            raycaster.setFromCamera(pointer, this.camera);
            const intersects = raycaster.intersectObject(this.arrowPivot, true);
            if (intersects.length > 0) {
                const touch = event.touches && event.touches[0];
                const clientX = touch ? touch.clientX : event.clientX;
                const clientY = touch ? touch.clientY : event.clientY;
                const start = getCanvasPoint(event, rect);
                this.launchTelemetry = {
                    version: 1,
                    input: event.type.startsWith('touch') ? 'touch' : 'mouse',
                    startedAt: performance.now(),
                    start,
                    moves: [],
                    viewport: {
                        width: Math.round(rect.width),
                        height: Math.round(rect.height),
                    },
                };
               
                this.isDragging = true;
                this.controls.enabled = false;
                this.renderer.domElement.style.cursor = 'grabbing';
                document.body.classList.add('grabbing');
                dragStartY = event.clientY || event.touches[0].clientY;
                dragEndY = dragStartY;
            }
        };

        const drag = (event) => {
            if (this.TIME.paused) return;
            if (!this.isDragging) return;
            const currentY = event.clientY || event.touches[0].clientY;
            const rawDragDistance = (dragStartY - currentY) / 100;


            // Clamp drag distance based on direction
            const dragDistance = rawDragDistance > 0
                ? Math.min(rawDragDistance, maxDragDistanceUp) // Upward drag
                : Math.max(rawDragDistance, -maxDragDistanceDown); // Downward drag

            this.arrow.position.z = 0 - dragDistance;
            const colorIntensity = Math.min(Math.abs(dragDistance) / Math.max(maxDragDistanceUp, maxDragDistanceDown), 1); // Normalize intensity
            dragEndY = currentY;

            if (this.launchTelemetry && this.launchTelemetry.moves.length < 24) {
                const elapsed = Math.round(performance.now() - this.launchTelemetry.startedAt);
                const lastMove = this.launchTelemetry.moves[this.launchTelemetry.moves.length - 1];

                // Keep one movement sample every 50ms: enough to inspect a gesture without
                // turning a single kick into an unnecessarily large request.
                if (!lastMove || elapsed - lastMove.t >= 50) {
                    this.launchTelemetry.moves.push({
                        t: elapsed,
                        ...getCanvasPoint(event, this.renderer.domElement.getBoundingClientRect()),
                    });
                }
            }
            this.renderer.domElement.style.cursor = 'grabbing';
            document.body.classList.add('grabbing');
            this.arrow.traverse((child) => {
                if (child.isMesh && child.name !== 'Text001') {
                    child.material.color.setRGB(0.9, 1.2 + colorIntensity, 1.2 + colorIntensity);
                }
            });
        };

        const endDrag = (event) => {
            if (this.TIME.paused) return;
            if (!this.isDragging) return;
            this.isDragging = false;
            this.controls.enabled = true;
            this.renderer.domElement.style.cursor = 'grab';
            document.body.classList.remove('grabbing');
            this.arrow.position.z = 0;
            this.arrow.traverse((child) => {
                if (child.isMesh && child.name !== 'Text001') {
                    child.material.color.set(0x84C3FC);
                }
            });
            const strength = Math.min(
                2 * (-maxDragDistanceDown / 1.5) * Math.min((dragStartY - dragEndY) / 100, maxDragDistanceDown),
                1.3
            );

            if (this.launchTelemetry) {
                const rect = this.renderer.domElement.getBoundingClientRect();
                const end = getCanvasPoint(event, rect);
                this.launchTelemetry.end = end;
                this.launchTelemetry.duration_ms = Math.round(performance.now() - this.launchTelemetry.startedAt);
                this.launchTelemetry.drag_distance_px = Math.round(dragStartY - dragEndY);
                this.launchTelemetry.move_count = this.launchTelemetry.moves.length;
                delete this.launchTelemetry.startedAt;
            }

            // Max out the strength so it doesn't go too high
            console.log(`%c Launching with strength: ${strength}`, 'color: #A6E3A1; font-weight: bold;');

            this.launch(parseFloat(strength * this.config.ball.launch_strength).toFixed(2));
        };

        // Add event listeners
        this.renderer.domElement.addEventListener('mousedown', startDrag);
        this.renderer.domElement.addEventListener('mousemove', drag);
        this.renderer.domElement.addEventListener('mouseup', endDrag);

        this.renderer.domElement.addEventListener('touchstart', startDrag);
        this.renderer.domElement.addEventListener('touchmove', drag);
        this.renderer.domElement.addEventListener('touchend', endDrag);

        this.TIME.addEventListener('tick', () => {

            globalUniforms.time.value = this.TIME.elapsed / 1000;

            if (this.shotClock !== undefined) {
                const elapsedTime = this.TIME.elapsed - this.shotClock; // Use Time's elapsed property
                const remainingTime = this.level_data.time_limit - elapsedTime;

                if (remainingTime <= 100 && !this.isHandled) {
                    this.handleMiss('Times up!');
                    this.shotClock = null;
                }

                const seconds = Math.max(0, Math.floor((remainingTime % 60000) / 1000));

                // this.text.visible = seconds < 10;
                this.text.opacity = (this.isHandled || seconds === 0) ? 0 : (seconds < 5 ? 1 : 0.5);

                const { color, outlineColor } = seconds < 3
                    ? { color: 0xFF5555, outlineColor: 0x862121 }
                    : seconds < 5
                        ? { color: 0xFAE2AF, outlineColor: 0x9f8142 }
                        : { color: 0x5CACD8, outlineColor: 0x2C3947 };

                Object.assign(this.text, { color, outlineColor, text: `${seconds}` });
                this.text.sync();
            }
        });

        document.addEventListener('click', (event) => {
            if (document.body.classList.contains('tutorial-horizontal-active')) {
                document.body.classList.remove('tutorial-horizontal-active');
                setTimeout(() => {
                    document.body.classList.add('tutorial-vertical-active');
                }, 500);
            }

            if (document.body.classList.contains('tutorial-vertical-active')) {
                document.body.classList.remove('tutorial-vertical-active');
                document.body.classList.remove('intro');
                this.TIME.resume();
            }

            if (document.body.classList.contains('tutorial-coin-active')) {
                document.body.classList.remove('tutorial-coin-active');
                this.TIME.resume();
            }

            if (document.body.classList.contains('tutorial-wind-active')) {
                document.body.classList.remove('tutorial-wind-active');
                this.TIME.resume();
            }
        })
    }

    /**
     * Launch the ball as a dynamic physics body along the camera's look direction.
     * No-ops if the ball is already in motion.
     * @param {number} [launch_strength=this.config.ball.launch_strength] - Impulse strength for the kick.
     */
    launch(launch_strength = this.config.ball.launch_strength) {

        if (this.ballInMotion) return;

        // Reset the shot clock
        this.shotClock = null;

        // Ball is in motion
        this.ballInMotion = true;

        // Play kick sound
        this.SOUNDS.play('kick');

        // Reset angular velocity and force
        this.BALL.body.angularVelocity.set(0, 0, 0);
        this.BALL.body.force.set(0, 0, 0);

        // Set mass and update mass properties
        this.BALL.body.type = CANNON.Body.DYNAMIC;
        this.BALL.body.mass = this.config.ball.mass;
        this.BALL.body.updateMassProperties();

        // Calculate launch direction
        const direction = new Vector3();
        this.camera.getWorldDirection(direction).multiplyScalar(launch_strength);

        // Compute launch angle
        const horizontalLen = Math.hypot(direction.x, direction.z);
        const launchAngle = launch_strength * (1 + Math.atan2(direction.y, horizontalLen));

        // Apply impulse
        const impulse = new CANNON.Vec3(direction.x, launchAngle, direction.z);
        this.BALL.body.applyImpulse(impulse, new CANNON.Vec3(0, .2, 0));

        // Clamp angular velocity
        const av = this.BALL.body.angularVelocity;
        const maxAV = this.config.ball.max_angular_velocity;
        this.BALL.body.angularVelocity.set(
            Math.max(-maxAV, Math.min(maxAV, av.x)),
            Math.max(-maxAV, Math.min(maxAV, av.y)),
            Math.max(-maxAV, Math.min(maxAV, av.z))
        );
    }


    /**
     * Debug helper (currently disabled) for rendering a wind-strength readout in the DOM.
     * @param {Vector3} windForce - Current wind force vector.
     */
    updateWindDisplay(windForce) {
        //     const windStrength = Math.sqrt(windForce.x * windForce.x + windForce.z * windForce.z);

        //     // Update DOM element
        //     let windDisplay = document.querySelector('.wind-display');
        //     if (!windDisplay) {
        //         windDisplay = document.createElement('div');
        //         windDisplay.className = 'wind-display';
        //         windDisplay.style.cssText = `
        //         position: fixed;
        //         top: 20px;
        //         right: 20px;
        //         background: rgba(0,0,0,0.7);
        //         color: white;
        //         padding: 10px;
        //         border-radius: 8px;
        //         font-family: monospace;
        //         z-index: 1000;
        //     `;
        //         document.body.appendChild(windDisplay);
        //     }

        //     windDisplay.innerHTML = `
        //     <div>Wind: ${windStrength.toFixed(2)}</div>
        // `;
    }
    /**
     * Main per-frame render/update loop: advances physics and wind, updates
     * camera follow and the ball/arrow/ring transforms, checks for a miss
     * (stopped or out-of-bounds ball), renders the post-processing composer,
     * and re-schedules itself via the renderer's animation loop.
     */
    render() {
        if (this.config.game.stats) this.STATS.begin();

        this.monitorFPS();

        const currentTime = performance.now();
        const deltaTime = (currentTime - this.last_time) / 1000; // Convert to seconds
        this.last_time = currentTime;

        const windForce = this.calculateWindForce(this.config.wind.direction, this.config.wind.intensity);

        this.updateWindDisplay(windForce);

        if (!this.isDragging) this.controls.update();

        this.PHYSICS.update();

        this.BALL.object.position.copy(this.BALL.body.position);
        this.BALL.object.quaternion.copy(this.BALL.body.quaternion);

        if (this.ballInMotion && this.config.game.follow) {
            const targetPosition = this.BALL.object.position.clone().add(new Vector3(0, 2, 5));
            this.camera.position.lerp(targetPosition, this.config.camera.smoothing);
            this.camera.lookAt(this.BALL.object.position.clone().add(new Vector3(0, 1, 0)));
        }

        if (this.ballInMotion) {

            // Scale wind force based on height
            const ballHeight = this.BALL.body.position.y;
            let heightFactor = 0;

            if (ballHeight >= .05) {
                heightFactor = Math.min((ballHeight - .05) / 10, 1); // Gradually increase effect up to a max factor of 1

                // Smooth the wind force application with lerping
                if (!this.lastWindForce) {
                    this.lastWindForce = new CANNON.Vec3(0, 0, 0);
                }

                const targetWindForce = new CANNON.Vec3(
                    windForce.x * this.config.ball.shear * heightFactor * 100,
                    windForce.y * this.config.ball.shear * heightFactor * 100,
                    windForce.z * this.config.ball.shear * heightFactor * 100
                );
                const windForceCannon = new CANNON.Vec3(
                    windForce.x * this.config.ball.shear * heightFactor * 100,
                    windForce.y * this.config.ball.shear * heightFactor * 100,
                    windForce.z * this.config.ball.shear * heightFactor * 100
                );

                // Lerp the wind force for smoother application
                const lerpFactor = 0.1; // Adjust for more/less smoothing
                this.lastWindForce.x = MathUtils.lerp(this.lastWindForce.x, targetWindForce.x, lerpFactor);
                this.lastWindForce.y = MathUtils.lerp(this.lastWindForce.y, targetWindForce.y, lerpFactor);
                this.lastWindForce.z = MathUtils.lerp(this.lastWindForce.z, targetWindForce.z, lerpFactor);

                const currentVelocity = this.BALL.body.velocity.clone();
                const adjustedVelocity = new CANNON.Vec3(
                    currentVelocity.x + this.lastWindForce.x * deltaTime,
                    currentVelocity.y + this.lastWindForce.y * deltaTime,
                    currentVelocity.z + this.lastWindForce.z * deltaTime
                );
                this.BALL.body.velocity.set(adjustedVelocity.x, adjustedVelocity.y, adjustedVelocity.z);



            }

            if (this.BALL.body.velocity.length() < 0.2) {
                if (!this.hasHit && !this.isHandled) this.handleMiss();
            }

            const pos = this.BALL.body.position;
            const groundWidth = this.config.ground.width / 0.98;
            const groundHeight = this.config.ground.height / 0.98;

            if (
                pos.y < 0.01 || pos.y > 20 ||
                pos.x < -groundWidth || pos.x > groundWidth ||
                pos.z < -groundHeight || pos.z > groundHeight
            ) {
                if (!this.hasHit && !this.isHandled) this.handleMiss('Out of bounds');
            }
        }

        this.ring.position.set(this.BALL.object.position.x, this.ring.position.y, this.BALL.object.position.z);

        const cameraDirection = new Vector3();
        this.camera.getWorldDirection(cameraDirection);

        const arrowRotation = Math.atan2(cameraDirection.x, cameraDirection.z) + Math.PI;
        this.arrowPivot.rotation.y = arrowRotation;

        if (!this.ballInMotion) {
            this.BALL.body.quaternion.setFromAxisAngle(new CANNON.Vec3(0, 1, 0), arrowRotation);
            const additionalRotation = new CANNON.Quaternion();
            additionalRotation.setFromAxisAngle(new CANNON.Vec3(1, 0, 0), Math.PI / 1.4); // Adjust the angle as needed
            this.BALL.body.quaternion = this.BALL.body.quaternion.mult(additionalRotation);
        }

        if (this.config.game.debug) this.debugger.update();

        // this.renderer.render(this.scene, this.camera);

        this.passes.composer.render()

        if (this.config.game.stats) this.STATS.end();

        this.renderer.setAnimationLoop(() => this.render());

    }

    /**
     * Switch to the given level (or a random level once all levels are
     * completed): loads its data, applies its wind/rain settings, resets
     * the scene, and shows any first-time tutorials.
     * @param {number} [level] - 1-based level number; falls back to the current/first level when omitted.
     * @param {boolean} [adjust_camera=true] - When true, shows a "Level N" toast notification.
     */
    changeLevels(level, adjust_camera = true) {

        if (this.STATE.data?.completed_all_levels) {
            level = 'Random'
            document.querySelector('.js-current-level').innerHTML = 'RANDOM'
        }

        if (!level) level = this.STATE.getLevel();
        if (!level) level = 1

        if (this.STATE.data?.completed_all_levels) {
            this.level_data = this.STATE.getRandomLevel();
        } else {
            this.level_data = window.levels[level - 1];
            this.STATE.setLevel(level);
        }


        if (!this.level_data) {
            console.warn(`%c No level data found for level ${level}`, 'color: #FF5555');
            return;
        }

        if (adjust_camera) window.Toast.fire({ title: `Level ${level}`, icon: 'info' });

        this.SOUNDS.play('woosh_in');
        this.shotClock = 0;
        this.TIME.resetTime();
        this.config.wind.direction = this.level_data.wind.direction;
        this.config.wind.intensity = this.level_data.wind.intensity;

        this.addRain(this.level_data.rain.intensity);

        this.reset();

        this.showFirstTimeTutorials();

    }

    /**
     * Pause the game and show the coin or wind tutorial overlay the first
     * time a level introduces that mechanic. No-op during the intro.
     */
    showFirstTimeTutorials() {

        if (document.body.classList.contains('intro')) return;

        if (this.level_data?.coins?.length && !this.hasSeenCoinTutorial) {
            this.hasSeenCoinTutorial = true;
            this.TIME.pause();
            document.body.classList.add('tutorial-coin-active');
            return;
        }

        if (this.level_data?.wind?.intensity?.max > 0 && !this.hasSeenWindTutorial) {
            this.hasSeenWindTutorial = true;
            this.TIME.pause();
            document.body.classList.add('tutorial-wind-active');
        }

    }

    /**
     * Advance to the next level (or replay the current one).
     * @param {boolean} [progress=false] - Currently only affects logging; level change logic is the same either way.
     */
    endLevel(progress = false) {

        if (progress) {
            this.changeLevels(this.STATE.getLevel());
            console.log(`%c Moving on to level: ${this.STATE.getLevel()}`, 'color: #A6E3A1');
        } else {
            this.changeLevels(this.STATE.getLevel());
        }
    }


    /**
     * @returns {string} A random positive exclamation for successful-hit toasts.
     */
    getRandomPositiveWord() {
        const positiveWords = ['Awesome!', 'Well done!', 'Fantastic!', 'Golden!', 'Superb!', 'Excellent!', 'Brilliant!', 'Amazing!', 'Incredible!', 'Outstanding!'];
        return positiveWords[Math.floor(Math.random() * positiveWords.length)];
    }

    /**
     * Handle a successful hit: play feedback, report the completed level to
     * the API, update state/score, and either celebrate finishing all
     * levels or progress to the next one. Restarts the game on API errors
     * indicating the match session is invalid.
     * @param {*} event - Unused; kept for call-site compatibility with collision callbacks.
     * @param {number} [points=100] - Points earned for this hit, reported to the API.
     */
    handleHit(event, points = 100) {

        this.isHandled = true;
        this.hasHit = true;
        this.PANE.animate();
        this.SOUNDS.play('point');

        setTimeout(() => {
            if (this.ring_hit) this.SOUNDS.play('coin');

            this.API.post('/complete_level', {
                level: this.STATE.getLevel(),
                points_earned: points,
                ring_hit: this.ring_hit || false,
                time_taken: this.TIME.elapsed,
                play_telemetry: this.launchTelemetry,
            }).then((data) => {
                this.ring_hit = false;
                this.STATE.updateState(data);
                this.STATE.setLevel(parseInt(data.data.current_kick_level_number));

                this.updateScore();

                if (data.data.completed_all_levels === true) {
                    setTimeout(() => {
                        window.Toast.fire({ title: `Congrats!`, text: 'You have completed all of the levels', timer: 8000, icon: 'success' });
                        this.SOUNDS.play('woosh_in');
                        this.reset();
                    }, 500);

                } else {
                    setTimeout(() => {
                        window.Toast.fire({ title: this.getRandomPositiveWord(), icon: 'success' });
                        this.SOUNDS.play('woosh_in');
                    }, 500);

                    setTimeout(() => this.endLevel(true), 2000);

                }
                console.log(data)

            }).catch((error) => {
                this.changeLevels(this.STATE.getLevel(), false);

                if (error.message.includes('Incorrect match type')) {
                    console.log('Incorrect match type')
                    this.restart('Incorrect match type');
                }

                if (error.message.includes('There is no active match.')) {
                    console.log('There is no active match.')
                    this.restart('There is no active match.');
                }

                console.log(`\n%c Error completing level:`, "color:#fff;font-size:12px; padding:0.45rem 0.75rem; margin: 0rem auto 1rem auto; font-family: Helvetica, sans-serif; border: 2px solid #0dd8d8; border-radius: 4px;font-weight: 100;background-size: cover;background-repeat: no-repeat;border: double 1px transparent;background-image: linear-gradient(#282828, #282828), radial-gradient(circle at top left, #2563EB, #82a0e2);background-origin: border-box;background-clip: padding-box, border-box;")
                console.log(error.message);

            });

        }, 100)
    }

    /**
     * Handle a missed shot: play feedback, report the failed level to the
     * API, update state/score, show a toast, and move on to the next level.
     * @param {string} [title='Miss!'] - Toast title describing the miss reason.
     */
    handleMiss(title = 'Miss!') {

        this.isHandled = true;

        this.SOUNDS.play('negative');

        this.API.post('/fail_level').then((data) => {
            this.STATE.updateState(data);
            this.STATE.setLevel(parseInt(data.data.current_kick_level_number));

            this.updateScore();

            setTimeout(() => {
                window.Toast.fire({ title, icon: 'error' });
                this.SOUNDS.play('woosh_in');
            }, 500);

            setTimeout(() => this.endLevel(), 1500);
        }).catch((error) => {
            this.endLevel();
            console.log(`\n%c Error completing level:`, "color:#fff;font-size:12px; padding:0.45rem 0.75rem; margin: 0rem auto 1rem auto; font-family: Helvetica, sans-serif; border: 2px solid #0dd8d8; border-radius: 4px;font-weight: 100;background-size: cover;background-repeat: no-repeat;border: double 1px transparent;background-image: linear-gradient(#282828, #282828), radial-gradient(circle at top left, #2563EB, #82a0e2);background-origin: border-box;background-clip: padding-box, border-box;")
            console.log(error.message);
        });



    }

}
